// Shop hours against a window. The people, their hours, blocked time and the efficiency
// factor are a setting ("capacity"), edited on the Plan screen's Capacity tab; nothing here
// assumes a number.

import type { JobSummary } from './jobs';

export interface Capacity {
  people: { name: string; hours_per_week: number; labor_class?: string; starts?: string | null }[];
  efficiency: number;
  blocks: { start: string; end: string; note?: string }[];
}

export function readCapacity(value: unknown): Capacity | null {
  const v = value as Partial<Capacity> | null | undefined;
  if (!v || !Array.isArray(v.people) || v.people.length === 0) return null;
  return { people: v.people, efficiency: Number(v.efficiency ?? 1) || 1, blocks: Array.isArray(v.blocks) ? v.blocks : [] };
}

const day = (s: string) => new Date(`${s.slice(0, 10)}T12:00:00`).getTime();
const DAY = 86400000;

/** Working days (Mon–Fri) in a range, less blocked days. */
function workingDays(start: string, end: string, blocks: Capacity['blocks']): number {
  let n = 0;
  for (let t = day(start); t <= day(end); t += DAY) {
    const d = new Date(t);
    if (d.getDay() === 0 || d.getDay() === 6) continue;
    if (blocks.some((b) => t >= day(b.start) && t <= day(b.end))) continue;
    n++;
  }
  return n;
}

/** Hours the shop has in a window, after efficiency. */
export function capacityHours(cap: Capacity, start: string, end: string): number {
  if (!start || !end || day(end) < day(start)) return 0;
  let total = 0;
  for (const p of cap.people) {
    const from = p.starts && day(p.starts) > day(start) ? p.starts : start;
    if (day(from) > day(end)) continue;
    total += (workingDays(from, end, cap.blocks) * Number(p.hours_per_week || 0)) / 5;
  }
  return total * cap.efficiency;
}

/** A job's labor hours that fall inside a window, spread evenly over the job's own window. */
export function hoursInWindow(j: JobSummary, start: string, end: string): number {
  const ws = j.opp?.window_start as string | null;
  const we = j.opp?.window_end as string | null;
  const total = Number(j.res?.totals.labor_hours ?? 0);
  if (!ws || !we || !total) return 0;
  const a = Math.max(day(ws), day(start));
  const b = Math.min(day(we), day(end));
  if (b < a) return 0;
  const span = day(we) - day(ws) + DAY;
  return (total * (b - a + DAY)) / span;
}

/** Likelihood as a 0–1 weight: a possible job reserves hours in proportion. */
export function weight(j: JobSummary): number {
  // one definition of "how sure": the engine's tier. Firm and committed count in full.
  if (j.tier === 'firm' || j.tier === 'committed') return 1;
  if (j.tier === 'none') return 0;
  const p = Number(j.opp?.likelihood_pct ?? 0);
  return Math.max(0, Math.min(1, p / 100));
}
